import * as React from 'react';
import { DropdownOption } from '@ixicare/ui';
import { codeToLanguage, Profile } from '@ixicare/resources';
import { useMutation } from '@tanstack/react-query';
import { Languages as LanguagesIcon } from 'lucide-react';
import i18next from 'i18next';
import { setLanguage, t } from '~/i18n';
import { useStores } from '~store/utils';
import LanguageModal from './LanguageModal';

const useI18nLanguage = () => {
  const [code, setCode] = React.useState(i18next.language);
  React.useEffect(() => {
    const handler = (lng: string) => setCode(lng);
    i18next.on('languageChanged', handler);
    return () => i18next.off('languageChanged', handler);
  }, []);
  return code;
};

interface LanguageDropdownEntry {
  entry: DropdownOption;
  modal: React.ReactNode;
}

export const useLanguageDropdownEntry = (): LanguageDropdownEntry => {
  const { auth } = useStores();
  const activeLanguageCode = useI18nLanguage();
  const [open, setOpen] = React.useState(false);

  const persistLanguage = useMutation({
    mutationFn: ({ profileId, language }: { profileId: number; language: ReturnType<typeof codeToLanguage> }) =>
      Profile.updateProfile(profileId, { language }),
    onSuccess: (data) => {
      auth.user?.update({ language: data.language });
      location.reload();
    },
  });

  const handlePick = React.useCallback(
    (code: string) => {
      setLanguage(code);
      if (!auth.user || auth.isImpersonating) {
        location.reload();
        return;
      }
      const language = codeToLanguage(code);
      if (!language) return;
      persistLanguage.mutate({ profileId: auth.user.id, language });
    },
    [auth.user, auth.isImpersonating, persistLanguage]
  );

  const entry: DropdownOption = {
    label: t('global.language'),
    lucide: LanguagesIcon,
    onClick: () => setOpen(true),
  };

  const modal = open ? (
    <LanguageModal activeCode={activeLanguageCode} onSelect={handlePick} onClose={() => setOpen(false)} />
  ) : null;

  return { entry, modal };
};
